import React, { useEffect, useMemo, useState } from 'react'
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd'
import {
  $permission,
  $isSuccessEdit,
  closeModal,
  permissionMounted,
  submitEditPermission,
  permissionRoleMounted,
  attachRoleMounted,
  detachRoleMounted,
} from './model'
import { useUnit } from 'effector-react'
import { useNavigate } from 'react-router-dom'
import { path } from '../../../../libs'
import { rolesObj } from '../../../../libs/role'

const { Text } = Typography

interface FormData {
  name: string
  description: string
}

export const rolesArray = Object.entries(rolesObj).map(([, value]) => ({
  value: value,
  label: value,
}))

export const EditPermission = () => {
  const navigate = useNavigate()
  const permission = useUnit($permission)
  const [form] = Form.useForm<FormData>()

  const [role, setRole] = useState<string | undefined>(undefined)
  const [detachRole, setDetachRole] = useState<string | undefined>(undefined)

  const [isSuccessEdit, modalCloseFn] = useUnit([$isSuccessEdit, closeModal])

  useEffect(() => {
    permissionMounted()
    permissionRoleMounted()
  }, [])

  useEffect(() => {
    form.setFieldsValue({
      name: permission.name,
      description: permission.description,
    })
  }, [permission, form])

  useEffect(() => {
    if (isSuccessEdit) {
      navigate(path.permissions.entry())
      modalCloseFn()
    }
  }, [isSuccessEdit])

  const attachedRoleIds = useMemo(
    () => new Set(permission.roles.map((r) => r.id)),
    [permission.roles],
  )

  const availableAttachOptions = useMemo(
    () =>
      rolesArray.filter((item) => !attachedRoleIds.has(item.value)),
    [attachedRoleIds],
  )

  const onFinish = (values: FormData) => {
    submitEditPermission({ id: permission.id, ...values })
  }

  const handleAttachRoles = () => {
    if (!role) return
    attachRoleMounted({ id: permission.id, role })
    setRole(undefined)
  }

  const handleDetachRoles = () => {
    if (!detachRole) return
    detachRoleMounted({ id: permission.id, role: detachRole })
    setDetachRole(undefined)
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Card title={<strong>Edit permission</strong>}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          style={{ maxWidth: 520 }}
        >
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: 'Имя обязательно' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input />
          </Form.Item>

          <Row gutter={8} align="bottom" style={{ marginBottom: 12 }}>
            <Col flex="auto">
              <Text style={{ display: 'block', marginBottom: 4 }}>
                Attach role
              </Text>
              <Select
                style={{ width: '100%' }}
                placeholder="Choose role"
                value={role}
                onChange={setRole}
                options={availableAttachOptions}
                showSearch
                optionFilterProp="label"
                allowClear
              />
            </Col>
            <Col>
              <Button disabled={!role} onClick={handleAttachRoles}>
                Attach role
              </Button>
            </Col>
          </Row>

          <Row gutter={8} align="bottom" style={{ marginBottom: 16 }}>
            <Col flex="auto">
              <Text style={{ display: 'block', marginBottom: 4 }}>
                Roles in permission
              </Text>
              <Select
                style={{ width: '100%' }}
                placeholder="Choose role"
                value={detachRole}
                onChange={setDetachRole}
                options={permission.roles.map((r) => ({
                  value: r.id,
                  label: r.id,
                }))}
                showSearch
                optionFilterProp="label"
                allowClear
              />
            </Col>
            <Col>
              <Button danger disabled={!detachRole} onClick={handleDetachRoles}>
                Detach role
              </Button>
            </Col>
          </Row>

          <Form.Item>
            <Button type="primary" htmlType="submit">
              Save
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Card
        title="Where used"
        extra={
          <Text type="secondary" style={{ fontSize: 12 }}>
            Roles and users that directly reference this permission
          </Text>
        }
      >
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
            Roles
          </Text>
          {permission.roles.length === 0 ? (
            <Text type="secondary">Not attached to any role</Text>
          ) : (
            <Space wrap>
              {permission.roles.map((r) => (
                <Tag
                  key={r.id}
                  color="blue"
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(path.roles.details(r.id))}
                >
                  {r.id}
                </Tag>
              ))}
            </Space>
          )}
        </div>
        <div>
          <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
            Users (extra)
          </Text>
          {!permission.extraUsers || permission.extraUsers.length === 0 ? (
            <Text type="secondary">
              No users have this as an extra permission
            </Text>
          ) : (
            <Space wrap>
              {permission.extraUsers.map((u) => (
                <Tag
                  key={u.id}
                  color="purple"
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(path.users.details(u.id))}
                >
                  {u.fullName || u.username || u.email || u.id}
                </Tag>
              ))}
            </Space>
          )}
        </div>
      </Card>
    </Space>
  )
}
